import React, { useMemo, useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  dashboardService,
  type ClaimEfficiency,
  type PeriodType,
} from "../../services/dashboard.service";
import { SearchableMultiSelect } from "../ui/SearchableMultiSelect";
import { Button } from "../ui/Button";
import { Select } from "../ui/Select";
import { DatePicker } from "../ui/DatePicker";

interface EfficiencyRow {
  name: string;
  unbilledCount: number;
  billedCount: number;
}

type SortField = "name" | "billed" | "unbilled";
type SortDirection = "asc" | "desc" | null;

// Helpers for date formatting and defaults
const formatDate = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = now; // today
  return { startDate: formatDate(start), endDate: formatDate(end) };
};

export const ClaimEfficiencyWidget: React.FC = () => {
  const [selectedDoctors, setSelectedDoctors] = useState<string[]>([]);
  const [sortField, setSortField] = useState<SortField | null>("billed");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [periodType, setPeriodType] = useState<PeriodType>("current_month");
  const [dateRange, setDateRange] = useState(() => getDefaultMonthRange());
  const [appliedDateRange, setAppliedDateRange] = useState(() => getDefaultMonthRange());
  const initializedSelectionRef = useRef(false);

  // Compute start/end date from selector
  const { startDate, endDate } = useMemo(() => {
    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    if (periodType === "custom") {
      return { startDate: appliedDateRange.startDate, endDate: appliedDateRange.endDate };
    }

    if (periodType === "current_week") {
      const now = new Date();
      const dayOfWeek = now.getDay();
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const monday = new Date(now);
      monday.setDate(now.getDate() - daysToMonday);
      return { startDate: fmt(monday), endDate: fmt(now) };
    }

    if (periodType === "previous_week") {
      const now = new Date();
      const dayOfWeek = now.getDay();
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const currentMonday = new Date(now);
      currentMonday.setDate(now.getDate() - daysToMonday);

      const prevMonday = new Date(currentMonday);
      prevMonday.setDate(currentMonday.getDate() - 7);

      const prevSunday = new Date(currentMonday);
      prevSunday.setDate(currentMonday.getDate() - 1);

      return { startDate: fmt(prevMonday), endDate: fmt(prevSunday) };
    }

    if (periodType === "previous_month") {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { startDate: fmt(start), endDate: fmt(end) };
    }

    if (periodType === "year_to_date") {
      const now = new Date();
      const start = new Date(now.getFullYear(), 0, 1);
      return { startDate: fmt(start), endDate: fmt(now) };
    }

    if (periodType === "last_year") {
      const now = new Date();
      const start = new Date(now.getFullYear() - 1, 0, 1);
      const end = new Date(now.getFullYear() - 1, 11, 31);
      return { startDate: fmt(start), endDate: fmt(end) };
    }

    if (periodType === "last_90_days") {
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - 90);
      return { startDate: fmt(start), endDate: fmt(now) };
    }

    // current_month default
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { startDate: fmt(start), endDate: fmt(now) };
  }, [periodType, appliedDateRange]);

  // Fetch metrics
  const {
    data: metrics,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["claim-efficiency", startDate, endDate],
    queryFn: () => dashboardService.getClaimEfficiency(startDate, endDate),
  });

  // Parse metrics into rows
  const rows = useMemo(() => {
    const parsedRows: EfficiencyRow[] = [];

    metrics?.forEach((metric) => {
      const value = metric.value as ClaimEfficiency;
      if (value && typeof value === "object" && "unbilled_count" in value) {
        parsedRows.push({
          name: metric.metricName,
          unbilledCount: parseInt(value.unbilled_count) || 0,
          billedCount: parseInt(value.billed_count) || 0,
        });
      }
    });

    // Sort: Practice first, then doctors alphabetically
    return parsedRows.sort((a, b) => {
      if (a.name === "Practice") return -1;
      if (b.name === "Practice") return 1;
      return a.name.localeCompare(b.name);
    });
  }, [metrics]);

  // Initialize selection to all names only once on first data load.
  // If data updates later, keep user's selection but drop names that no longer exist.
  useEffect(() => {
    if (rows.length === 0) return;

    if (!initializedSelectionRef.current) {
      initializedSelectionRef.current = true;
      setSelectedDoctors(rows.map((r) => r.name));
      return;
    }

    // Ensure selection doesn't include removed names on subsequent updates
    setSelectedDoctors((prev) => prev.filter((n) => rows.some((r) => r.name === n)));
  }, [rows]);

  const allDoctorNames = rows.map((r) => r.name).filter((name): name is string => name != null);

  const filteredRows = useMemo(() => {
    if (selectedDoctors.length === 0) return [];
    const filtered = rows.filter((r) => selectedDoctors.includes(r.name));

    // If no sorting is applied, return original order
    if (!sortField || !sortDirection) {
      return filtered;
    }

    // Sort the filtered results
    return [...filtered].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      if (sortField === "name") {
        aValue = a.name;
        bValue = b.name;
      } else if (sortField === "billed") {
        aValue = a.billedCount;
        bValue = b.billedCount;
      } else {
        aValue = a.unbilledCount;
        bValue = b.unbilledCount;
      }

      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }, [rows, selectedDoctors, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  return (
    <div className="bg-white border-2 border-border-gray flex flex-col">
      {/* Header */}
      <div className="bg-primary-blue text-white px-4 md:px-6 py-3">
        <h3 className="font-bold text-lg">Claims Efficiency</h3>
        <span className="text-xs sm:text-sm">Billed / Unbilled</span>
      </div>

      {/* Filters */}
      <div className="p-4 bg-gray-50 border-b-2 border-border-gray flex flex-wrap gap-3 items-end">
        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 mb-1">Providers</label>
          <SearchableMultiSelect
            options={allDoctorNames}
            selectedOptions={selectedDoctors}
            onSelectionChange={setSelectedDoctors}
            label="Providers"
            showArrow={false}
            displayCount={Math.max(0, selectedDoctors.length - 1)}
          />
        </div>
        <div className="flex-shrink-0 min-w-[140px]">
          <label className="block text-xs font-medium text-gray-700 mb-1">Period</label>
          <Select
            value={periodType}
            onChange={(e) => setPeriodType(e.target.value as PeriodType)}
            className="w-full"
          >
            <option value="current_week">Current Week</option>
            <option value="previous_week">Previous Week</option>
            <option value="current_month">Current Month</option>
            <option value="previous_month">Previous Month</option>
            <option value="last_90_days">Last 90 Days</option>
            <option value="year_to_date">Year to Date</option>
            <option value="last_year">Last Year</option>
            <option value="custom">Custom Period</option>
          </Select>
        </div>

        {periodType === "custom" && (
          <>
            <div className="flex-shrink-0">
              <label className="block text-xs font-medium text-gray-700 mb-1">Start date</label>
              <DatePicker
                value={dateRange.startDate}
                maxDate={dateRange.endDate || undefined}
                onChange={(value) =>
                  setDateRange((prev: { startDate: string; endDate: string }) => ({
                    ...prev,
                    startDate: value,
                  }))
                }
              />
            </div>
            <div className="flex-shrink-0">
              <label className="block text-xs font-medium text-gray-700 mb-1">End date</label>
              <DatePicker
                value={dateRange.endDate}
                minDate={dateRange.startDate || undefined}
                onChange={(value) =>
                  setDateRange((prev: { startDate: string; endDate: string }) => ({
                    ...prev,
                    endDate: value,
                  }))
                }
              />
            </div>
            <button
              type="button"
              onClick={() => {
                const defaultRange = getDefaultMonthRange();
                setDateRange(defaultRange);
                setAppliedDateRange(defaultRange);
              }}
              className="h-10 px-3 bg-black text-white text-xs sm:text-sm hover:bg-gray-800 whitespace-nowrap flex-shrink-0"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={() => setAppliedDateRange(dateRange)}
              className="h-10 px-3 bg-primary-blue text-white text-xs sm:text-sm hover:bg-indigo-darker-blue whitespace-nowrap flex-shrink-0"
            >
              Apply
            </button>
          </>
        )}
      </div>

      {/* Content */}
      <div>
        {isLoading && (
          <div className="text-center py-8 text-gray-500 p-4 sm:p-6">
            Loading claims efficiency data...
          </div>
        )}

        {error && (
          <div className="text-center p-4 sm:p-6 py-8 text-red-600">
            Failed to load claims efficiency data. Please try again.
          </div>
        )}

        {!isLoading && !error && rows.length === 0 && (
          <div className="text-center p-4 sm:p-6 py-8 text-gray-500">No data available</div>
        )}

        {!isLoading && !error && filteredRows.length > 0 && (
          <div>
            <div className="max-h-[180px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-border-gray sticky top-0 z-10">
                  <tr>
                    <th
                      className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center justify-between">
                        <span>Provider</span>
                        {sortField === "name" && sortDirection && (
                          <span className="ml-1">{sortDirection === "asc" ? "↑" : "↓"}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort("billed")}
                    >
                      <div className="flex items-center justify-end">
                        <span>Billed</span>
                        {sortField === "billed" && sortDirection && (
                          <span className="ml-1">{sortDirection === "asc" ? "↑" : "↓"}</span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort("unbilled")}
                    >
                      <div className="flex items-center justify-end">
                        <span>Unbilled</span>
                        {sortField === "unbilled" && sortDirection && (
                          <span className="ml-1">{sortDirection === "asc" ? "↑" : "↓"}</span>
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-gray">
                  {filteredRows.map((row) => (
                    <tr key={row.name} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm font-medium">{row.name}</td>
                      <td className="px-4 py-2 text-sm text-right">{row.billedCount}</td>
                      <td className="px-4 py-2 text-sm text-right">{row.unbilledCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!isLoading && !error && filteredRows.length === 0 && rows.length > 0 && (
          <div className="text-center py-8 text-gray-500">No providers selected</div>
        )}
      </div>
    </div>
  );
};
